import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../../axios/axiosInstance";
import {
  coerceOptionalCredentialUrl,
  parseEmploymentTypeArray,
  resolveCertificationCredentialUrl,
  resolveCertificationNoExpiry,
  validateCertificationEntry,
  validateCredentialUrl,
  validateEducationEntry,
} from "../utils/profileFieldNormalization";

function RequiredLabel({ children }) {
  return (
    <label className="cpe-label">
      {children} <span className="cpe-required">*</span>
    </label>
  );
}

// ── Searchable Input Dropdown Component ──────────────────────────────────────────

function SearchableInput({
  label,
  value,
  onChange,
  placeholder,
  fetchUrl,
  displayKey = "name",
  valueKey = "name",
  formatDisplay,
  helperText,
  required = false,
  error = "",
}) {
  const [query, setQuery] = useState(value || "");
  const [options, setOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setOptions([]);
      setHasFetched(false);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axiosInstance.get(`${fetchUrl}${encodeURIComponent(query)}`);
        setOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching options:", err);
      } finally {
        setLoading(false);
        setHasFetched(true);
      }
    }, 200);

    return () => clearTimeout(delayDebounce);
  }, [query, fetchUrl, isOpen]);

  return (
    <div ref={wrapperRef} className="cpe-form-group" style={{ position: "relative" }}>
      {required ? (
        <RequiredLabel>{label}</RequiredLabel>
      ) : (
        <label className="cpe-label">{label}</label>
      )}
      <input
        className={`cpe-input${error ? " cpe-input-error" : ""}`}
        placeholder={placeholder}
        value={query}
        onFocus={() => setIsOpen(true)}
        onClick={() => setIsOpen(true)}
        onChange={(e) => {
          const val = e.target.value;
          setQuery(val);
          onChange(val);
          setIsOpen(true);
          setHasFetched(false);
        }}
      />
      {helperText && !error && <p className="cpe-helper">{helperText}</p>}
      {error && <p className="cpe-field-error">{error}</p>}
      {isOpen && (
        <ul className="cpe-dropdown-menu" style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          background: "white",
          border: "1px solid #d1d5db",
          borderRadius: "6px",
          maxHeight: "180px",
          overflowY: "auto",
          zIndex: 1000,
          margin: "4px 0 0",
          padding: "4px 0",
          listStyle: "none",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
        }}>
           {(loading || !hasFetched) && <li style={{ padding: "8px 12px", color: "#6b7280", fontSize: "13px" }}>Loading...</li>}
          {!loading && hasFetched && options.length === 0 && (
            <li style={{ padding: "8px 12px", color: "#6b7280", fontSize: "13px" }}>No results found</li>
          )}
          {options.map((opt, idx) => {
            const displayVal = formatDisplay ? formatDisplay(opt) : (opt[displayKey] || opt);
            const selectVal = opt[valueKey] || opt;
            return (
              <li
                key={idx}
                onClick={() => {
                  setQuery(displayVal);
                  onChange(selectVal);
                  setIsOpen(false);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontSize: "13px",
                  borderBottom: "1px solid #f3f4f6",
                  color: "#1f2937",
                  textAlign: "left"
                }}
                onMouseEnter={(e) => e.target.style.background = "#f3f4f6"}
                onMouseLeave={(e) => e.target.style.background = "transparent"}
              >
                {displayVal}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── Certification helpers ──────────────────────────────────────────────────────

function formatCertDate(dateStr) {
  if (!dateStr) return "";
  const trimmed = String(dateStr).trim();
  if (/^\d{2}\/\d{4}$/.test(trimmed)) return trimmed;
  const date = new Date(trimmed);
  if (!isNaN(date.getTime())) {
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    return `${month}/${year}`;
  }
  return trimmed;
}

function normalizeCertification(cert, credentialUrlOverride) {
  if (!cert) return null;
  const noExpiry = resolveCertificationNoExpiry(cert) === 1;
  const rawUrl =
    credentialUrlOverride !== undefined
      ? coerceOptionalCredentialUrl(credentialUrlOverride)
      : resolveCertificationCredentialUrl(cert);
  const urlCheck = validateCredentialUrl(rawUrl);

  return {
    id: cert.id,
    certificationName: cert.certificationName || cert.certification_name || "",
    issuingOrganization: cert.issuingOrganization || cert.issuing_organization || "",
    issueDate: formatCertDate(cert.issueDate || cert.issue_date),
    expiryDate: noExpiry ? "" : formatCertDate(cert.expiryDate || cert.expiry_date),
    noExpiry,
    credentialId: cert.credentialId || cert.credential_id || "",
    credentialUrl: urlCheck.valid ? urlCheck.normalized : "",
  };
}

// ── Dialogs ────────────────────────────────────────────────────────────────────

function EduDialog({ open, onClose, onSave, initial }) {
  const [form, setForm] = useState({ degree: "", fieldOfStudy: "", location: "" });
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(initial || { degree: "", fieldOfStudy: "", location: "" });
      setFieldErrors({});
    }
  }, [open, initial]);

  const handleSaveEducation = () => {
    const validation = validateEducationEntry(form);
    if (!validation.valid) {
      setFieldErrors(validation.errors);
      return;
    }
    onSave({
      degree: form.degree.trim(),
      fieldOfStudy: form.fieldOfStudy.trim(),
      location: form.location.trim(),
    });
    onClose();
  };

  if (!open) return null;
  return (
    <div className="cpe-modal-overlay" onClick={onClose}>
      <div className="cpe-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="cpe-modal-title">{initial ? "Edit Education" : "Add Education"}</h3>
        
        <SearchableInput
          label="Degree"
          required
          error={fieldErrors.degree}
          placeholder="Search or enter degree e.g., Bachelor of Science"
          value={form.degree}
          onChange={(val) => {
            setForm((prev) => ({ ...prev, degree: val }));
            if (fieldErrors.degree) {
              setFieldErrors((prev) => ({ ...prev, degree: "" }));
            }
          }}
          fetchUrl="/api/profile/programs?name="
          displayKey="name"
          valueKey="name"
        />

        <div className="cpe-form-group">
          <RequiredLabel>Field of Study</RequiredLabel>
          <input
            className={`cpe-input${fieldErrors.fieldOfStudy ? " cpe-input-error" : ""}`}
            placeholder="e.g., Computer Science"
            value={form.fieldOfStudy || ""}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, fieldOfStudy: e.target.value }));
              if (fieldErrors.fieldOfStudy) {
                setFieldErrors((prev) => ({ ...prev, fieldOfStudy: "" }));
              }
            }}
          />
          {fieldErrors.fieldOfStudy && (
            <p className="cpe-field-error">{fieldErrors.fieldOfStudy}</p>
          )}
        </div>

        <SearchableInput
          label="Institution / Location"
          required
          error={fieldErrors.location}
          placeholder="Search or enter institution e.g., ETH Zurich"
          value={form.location}
          onChange={(val) => {
            setForm((prev) => ({ ...prev, location: val }));
            if (fieldErrors.location) {
              setFieldErrors((prev) => ({ ...prev, location: "" }));
            }
          }}
          fetchUrl="/api/profile/institutes?name="
          displayKey="name"
          valueKey="name"
          formatDisplay={(opt) => (opt.city ? `${opt.name} (${opt.city})` : opt.name)}
        />

        <div className="cpe-modal-actions">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSaveEducation}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function CertDialog({ open, onClose, onSave, initial }) {
  const blank = { certificationName: "", issuingOrganization: "", issueDate: "", expiryDate: "", noExpiry: false, credentialId: "", credentialUrl: "" };
  const [form, setForm] = useState(blank);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(initial ? normalizeCertification(initial) : blank);
      setFieldErrors({});
    }
  }, [open, initial]);

  const handleSaveCertification = () => {
    const validation = validateCertificationEntry(form);
    if (!validation.valid) {
      setFieldErrors(validation.errors);
      return;
    }
    onSave(normalizeCertification(form, validation.normalizedUrl));
    onClose();
  };

  if (!open) return null;
  return (
    <div className="cpe-modal-overlay" onClick={onClose}>
      <div className="cpe-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="cpe-modal-title">{initial ? "Edit Certification" : "Add Certification"}</h3>
        
        <SearchableInput
          label="Certification Name"
          required
          error={fieldErrors.certificationName}
          placeholder="Search or enter certification name"
          value={form.certificationName}
          onChange={(val) => {
            setForm((prev) => ({ ...prev, certificationName: val }));
            if (fieldErrors.certificationName) {
              setFieldErrors((prev) => ({ ...prev, certificationName: "" }));
            }
          }}
          fetchUrl="/api/profile/certifications?name="
          displayKey="name"
          valueKey="name"
        />

        <SearchableInput
          label="Issuing Organization"
          required
          error={fieldErrors.issuingOrganization}
          placeholder="Search or enter issuing organization"
          value={form.issuingOrganization}
          onChange={(val) => {
            setForm((prev) => ({ ...prev, issuingOrganization: val }));
            if (fieldErrors.issuingOrganization) {
              setFieldErrors((prev) => ({ ...prev, issuingOrganization: "" }));
            }
          }}
          fetchUrl="/api/profile/certification-providers?name="
          displayKey="name"
          valueKey="name"
        />

        <div className="cpe-form-group">
          <label className="cpe-checkbox-item">
            <input
              type="checkbox"
              checked={Boolean(form.noExpiry)}
              onChange={(e) => {
                const checked = e.target.checked;
                setForm((prev) => ({
                  ...prev,
                  noExpiry: checked,
                  expiryDate: checked ? "" : prev.expiryDate,
                }));
                if (checked) {
                  setFieldErrors((prev) => ({ ...prev, expiryDate: "" }));
                }
              }}
            />
            No Expiry
          </label>
        </div>
        <div className="cpe-form-row">
          <div>
            <RequiredLabel>Issue Date</RequiredLabel>
            <input
              className={`cpe-input${fieldErrors.issueDate ? " cpe-input-error" : ""}`}
              placeholder="MM/YYYY"
              value={form.issueDate || ""}
              onChange={(e) => {
                let val = e.target.value.replace(/[^\d]/g, "");
                if (val.length >= 3) val = val.slice(0, 2) + "/" + val.slice(2, 6);
                if (val.length > 7) val = val.slice(0, 7);
                setForm((prev) => ({ ...prev, issueDate: val }));
                if (fieldErrors.issueDate) {
                  setFieldErrors((prev) => ({ ...prev, issueDate: "" }));
                }
              }}
            />
            {fieldErrors.issueDate && (
              <p className="cpe-field-error">{fieldErrors.issueDate}</p>
            )}
          </div>
          {!form.noExpiry && (
            <div>
              <RequiredLabel>Expiry Date</RequiredLabel>
              <input
                className={`cpe-input${fieldErrors.expiryDate ? " cpe-input-error" : ""}`}
                placeholder="MM/YYYY"
                value={form.expiryDate || ""}
                onChange={(e) => {
                  let val = e.target.value.replace(/[^\d]/g, "");
                  if (val.length >= 3) val = val.slice(0, 2) + "/" + val.slice(2, 6);
                  if (val.length > 7) val = val.slice(0, 7);
                  setForm((prev) => ({ ...prev, expiryDate: val }));
                  if (fieldErrors.expiryDate) {
                    setFieldErrors((prev) => ({ ...prev, expiryDate: "" }));
                  }
                }}
              />
              {fieldErrors.expiryDate && (
                <p className="cpe-field-error">{fieldErrors.expiryDate}</p>
              )}
            </div>
          )}
        </div>
        <div className="cpe-form-group">
          <label className="cpe-label">Credential ID</label>
          <input className="cpe-input" value={form.credentialId} onChange={(e) => setForm((p) => ({ ...p, credentialId: e.target.value }))} />
        </div>
        <div className="cpe-form-group">
          <label className="cpe-label">Credential URL</label>
          <input
            className={`cpe-input${fieldErrors.credentialUrl ? " cpe-input-error" : ""}`}
            type="url"
            inputMode="url"
            placeholder="https://www.example.com/credential"
            value={form.credentialUrl}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, credentialUrl: e.target.value }));
              if (fieldErrors.credentialUrl) {
                setFieldErrors((prev) => ({ ...prev, credentialUrl: "" }));
              }
            }}
            onBlur={() => {
              const trimmed = form.credentialUrl.trim();
              if (!trimmed) return;
              const urlCheck = validateCredentialUrl(trimmed);
              if (!urlCheck.valid) {
                setFieldErrors((prev) => ({
                  ...prev,
                  credentialUrl: urlCheck.message,
                }));
              }
            }}
          />
          <p className="cpe-helper">
            Optional. Must be a valid website link starting with http:// or https://
          </p>
          {fieldErrors.credentialUrl && (
            <p className="cpe-field-error">{fieldErrors.credentialUrl}</p>
          )}
        </div>
        <div className="cpe-modal-actions">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSaveCertification}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

const SchoolIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ verticalAlign: 'middle', marginRight: 4 }}>
    <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z" />
  </svg>
);

const WorkspacePremiumIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style={{ verticalAlign: 'middle', marginRight: 4 }}>
    <path d="m9.68 13.69-2.65.36a1 1 0 0 0-.83 1.34l1.38 2.69-1.26 2.82c-.17.39 0 .85.38 1.05.38.2.85.08 1.09-.27l1.79-2.5 2.86 1.06c.38.14.81-.02.99-.38.18-.36.08-.8-.26-1.04l-2.4-1.74.87-2.92a1 1 0 0 0-1.18-1.25l-2.82.52c-.44.08-.75.49-.67.94s.49.75.94.67l2.25-.41-.75 2.51c-.13.43.08.9.49 1.08l2.29.98-2.06-1.5c-.32-.23-.74-.28-1.1-.11l-2.61.97 1.13-2.54c.18-.4.07-.88-.27-1.16l-2.54-2.07 2.37-.32c.44-.06.75-.47.69-.91-.07-.44-.48-.75-.92-.69zm8.56 1.34a1 1 0 0 0-.83-1.34l-2.65-.36a1 1 0 0 0-.92.69c-.06.44.25.85.69.91l2.37.32-2.54 2.07c-.34.28-.45.76-.27 1.16l1.13 2.54-2.61-.97c-.36-.17-.78-.12-1.1.11l-2.06 1.5 2.29-.98c.41-.18.62-.65.49-1.08l-.75-2.51 2.25.41c.45.08.86-.33.94-.67a1 1 0 0 0-.67-.94l-2.82-.52a1 1 0 0 0-1.18 1.25l.87 2.92-2.4 1.74c-.34.24-.44.68-.26 1.04.18.36.61.52.99.38l2.86-1.06 1.79 2.5c.24.35.71.47 1.09.27.38-.2.55-.66.38-1.05l-1.26-2.82 1.38-2.69zm-6.24-4.8c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3z" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

// ── Main Component ─────────────────────────────────────────────────────────────

/**
 * CareerDetailsSection
 *
 * All CareerDetails fields — plain HTML/CSS.
 *   ✅ Visible to user:   CV upload, LinkedIn URL
 *   ⭐ Admin-only hidden: Current Company, Position, Employment Type,
 *                         Work Setup, Will Relocate, Education, Certifications
 */
const CareerDetailsSection = ({ profileData, userId, onChange }) => {
  const cd = profileData?.careerDetails || {};

  const [selectedFile, setSelectedFile] = useState(null);
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [currentPosition, setCurrentPosition] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [employmentType, setEmploymentType] = useState([]);
  const [workSetup, setWorkSetup] = useState([]);
  const [willRelocate, setWillRelocate] = useState("No");
  const [educationList, setEducationList] = useState([]);
  const [certificationList, setCertificationList] = useState([]);
  const [uploadError, setUploadError] = useState("");

  // Education/cert dialogs
  const [eduDialogOpen, setEduDialogOpen] = useState(false);
  const [editingEdu, setEditingEdu] = useState(null);
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [editingCert, setEditingCert] = useState(null);

  const workSetupOptions = [
    { id: 1, name: "On-site" },
    { id: 2, name: "Hybrid" },
    { id: 3, name: "Remote" },
  ];

  // Sync incoming data
  useEffect(() => {
    if (!cd) return;
    setLinkedinUrl(cd.linkedin_url || "");
    setCurrentPosition(cd.current_position || "");
    setSelectedCompany(cd.selectedCompanyName || cd.companyName || "");
    setEmploymentType(parseEmploymentTypeArray(cd.employmentType ?? cd.employment_type));
    setWorkSetup(cd.workSetup || []);
    setWillRelocate(cd.willRelocate || "No");
    setEducationList(cd.educations || []);
    setCertificationList((cd.certifications || []).map(normalizeCertification));
    if (cd.cv_filename) {
      setSelectedFile({ name: cd.cv_filename });
    }
  }, [profileData]);

  const emitChange = (overrides = {}) => {
    if (!onChange) return;
    onChange({
      linkedin_url: linkedinUrl,
      current_position: currentPosition,
      selectedCompanyName: selectedCompany,
      workSetup,
      willRelocate,
      educations: educationList,
      certifications: certificationList,
      cv_filename: selectedFile?.name || null,
      ...overrides,
      employmentType: parseEmploymentTypeArray(
        overrides.employmentType ?? employmentType,
      ),
    });
  };



  const handleFile = (file) => {
    if (!file) return;
    const ext = file.name.toLowerCase().split(".").pop();
    if (!["pdf", "docx", "doc"].includes(ext)) {
      setUploadError("Please upload a PDF or DOCX file.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setUploadError("File size must be less than 2 MB.");
      return;
    }
    setUploadError("");
    setSelectedFile(file);
    emitChange({ cv_filename: file.name });
  };

  const handleViewCV = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3500";
      const res = await fetch(`${apiUrl}/api/admin/candidates/${userId}/download-cv`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "ngrok-skip-browser-warning": "true",
        },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
    } catch (err) {
      console.error("View CV error:", err);
    }
  };

  const toggleEmploymentType = (type) => {
    const next = employmentType.includes(type)
      ? employmentType.filter((t) => t !== type)
      : [...employmentType, type];
    setEmploymentType(next);
    emitChange({ employmentType: next });
  };

  const toggleWorkSetup = (id) => {
    const next = workSetup.includes(id)
      ? workSetup.filter((i) => i !== id)
      : [...workSetup, id];
    setWorkSetup(next);
    emitChange({ workSetup: next });
  };

  return (
    <div>
      {/* Fields below are visible in user-frontend, so they are commented out here 
      <div className="cpe-form-group">
        <label className="cpe-label">CV / Resume</label>
        <div
          className="cpe-upload-area"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
        >
          <input
            type="file"
            accept=".pdf,.docx,.doc"
            className="cpe-upload-hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <span className="cpe-upload-icon">📄</span>
          <p className="cpe-upload-text">
            {selectedFile ? selectedFile.name : "Click to upload or drag & drop"}
          </p>
          <p className="cpe-upload-hint">PDF or DOCX (Max 2MB)</p>
          {selectedFile && (
            <div className="cpe-upload-actions">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={(e) => { e.stopPropagation(); handleViewCV(); }}
              >
                View CV
              </button>
              <button
                type="button"
                className="btn btn-sm"
                style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}
                onClick={(e) => { e.stopPropagation(); setSelectedFile(null); emitChange({ cv_filename: null }); }}
              >
                Remove
              </button>
            </div>
          )}
        </div>
        {uploadError && <p className="cpe-helper" style={{ color: "#dc2626" }}>{uploadError}</p>}
      </div>

      <div className="cpe-or-separator">OR</div>

      <div className="cpe-form-group">
        <label className="cpe-label">LinkedIn Profile URL</label>
        <input
          className="cpe-input"
          placeholder="https://linkedin.com/in/profile"
          value={linkedinUrl}
          onChange={(e) => { setLinkedinUrl(e.target.value); emitChange({ linkedin_url: e.target.value }); }}
        />
        <p className="cpe-helper">Add LinkedIn URL if no CV is available.</p>
      </div>
      */}



      {/* ── Current Company Name ── */}
      <SearchableInput
        label="Current Company Name"
        placeholder="e.g., Google, Microsoft…"
        value={selectedCompany}
        onChange={(val) => {
          setSelectedCompany(val);
          emitChange({ selectedCompanyName: val });
        }}
        fetchUrl="/api/corporate/profile/corporate-list?q="
        displayKey="name"
        valueKey="name"
        helperText="Ensures candidate doesn't appear for current employer"
      />

      {/* ── Current Position ── */}
      <div className="cpe-form-group">
        <label className="cpe-label">Current Position</label>
        <input
          className="cpe-input"
          placeholder="e.g., Senior Software Engineer"
          value={currentPosition}
          onChange={(e) => { setCurrentPosition(e.target.value); emitChange({ current_position: e.target.value }); }}
        />
      </div>

      {/* ── Employment Type ── */}
      <div className="cpe-form-group">
        <label className="cpe-label">Employment Type</label>
        <div className="cpe-checkbox-group horizontal">
          {["Full-time", "Part-time", "Contractor", "Freelancer"].map((type) => (
            <label key={type} className="cpe-checkbox-item">
              <input
                type="checkbox"
                checked={employmentType.includes(type)}
                onChange={() => toggleEmploymentType(type)}
              />
              {type}
            </label>
          ))}
        </div>
      </div>

      {/* ── Work Setup ── */}
      <div className="cpe-form-group">
        <label className="cpe-label">Location Type (Work Setup)</label>
        <div className="cpe-checkbox-group horizontal">
          {workSetupOptions.map((opt) => (
            <label key={opt.id} className="cpe-checkbox-item">
              <input
                type="checkbox"
                checked={workSetup.includes(opt.id)}
                onChange={() => toggleWorkSetup(opt.id)}
              />
              {opt.name}
            </label>
          ))}
        </div>
      </div>

      {/* ── Will Relocate ── */}
      <div className="cpe-form-group">
        <label className="cpe-label">Willing to Relocate?</label>
        <div className="cpe-radio-group horizontal">
          <label className="cpe-radio-item">
            <input type="radio" name="relocate" value="Yes" checked={willRelocate === "Yes"} onChange={() => { setWillRelocate("Yes"); emitChange({ willRelocate: "Yes" }); }} />
            Yes
          </label>
          <label className="cpe-radio-item">
            <input type="radio" name="relocate" value="No" checked={willRelocate === "No"} onChange={() => { setWillRelocate("No"); emitChange({ willRelocate: "No" }); }} />
            No
          </label>
        </div>
      </div>

      {/* ── Education ── */}
      <div className="cpe-card">
        <div className="cpe-card-header">
          <h4 className="cpe-card-title">
            <SchoolIcon /> Education
          </h4>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => { setEditingEdu(null); setEduDialogOpen(true); }}
          >
            + Add
          </button>
        </div>
        {educationList.length === 0 && (
          <p className="cpe-helper">No education records added.</p>
        )}
        {educationList.map((edu, i) => (
          <div key={edu.id || i} className="cpe-list-item">
            <div className="cpe-list-item-info">
              <p className="cpe-list-item-title">{edu.degree}</p>
              <p className="cpe-list-item-sub">{edu.fieldOfStudy}{edu.location ? ` — ${edu.location}` : ""}</p>
            </div>
            <div className="cpe-list-item-actions">
              <button className="cpe-icon-btn" onClick={() => { setEditingEdu(edu); setEduDialogOpen(true); }}>
                <EditIcon />
              </button>
              <button className="cpe-icon-btn danger" onClick={() => { const next = educationList.filter((e) => e.id !== edu.id); setEducationList(next); emitChange({ educations: next }); }}>
                <DeleteIcon />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── Certifications ── */}
      <div className="cpe-card" style={{ marginTop: 12 }}>
        <div className="cpe-card-header">
          <h4 className="cpe-card-title">
            <WorkspacePremiumIcon /> Certifications
          </h4>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => { setEditingCert(null); setCertDialogOpen(true); }}
          >
            + Add
          </button>
        </div>
        {certificationList.length === 0 && (
          <p className="cpe-helper">No certifications added.</p>
        )}
        {certificationList.map((cert, i) => (
          <div key={cert.id || i} className="cpe-list-item">
            <div className="cpe-list-item-info">
              <p className="cpe-list-item-title">{cert.certificationName}</p>
              <p className="cpe-list-item-sub">{cert.issuingOrganization}</p>
            </div>
            <div className="cpe-list-item-actions">
              <button className="cpe-icon-btn" onClick={() => { setEditingCert(cert); setCertDialogOpen(true); }}>
                <EditIcon />
              </button>
              <button className="cpe-icon-btn danger" onClick={() => { const next = certificationList.filter((c) => c.id !== cert.id); setCertificationList(next); emitChange({ certifications: next }); }}>
                <DeleteIcon />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Dialogs */}
      <EduDialog
        open={eduDialogOpen}
        onClose={() => setEduDialogOpen(false)}
        initial={editingEdu}
        onSave={(data) => {
          const next = editingEdu
            ? educationList.map((e) => (e.id === editingEdu.id ? { ...data, id: e.id } : e))
            : [...educationList, { ...data, id: Date.now() }];
          setEducationList(next);
          emitChange({ educations: next });
        }}
      />
      <CertDialog
        open={certDialogOpen}
        onClose={() => setCertDialogOpen(false)}
        initial={editingCert}
        onSave={(data) => {
          const next = editingCert
            ? certificationList.map((c) => (c.id === editingCert.id ? { ...data, id: c.id } : c))
            : [...certificationList, { ...data, id: Date.now() }];
          setCertificationList(next);
          emitChange({ certifications: next });
        }}
      />
    </div>
  );
};

export default CareerDetailsSection;
